/**
 * Client-side glasses / sunglasses detection via @huggingface/transformers.
 *
 * Uses a tiny ONNX MobileNetV4 image classifier (top-20 ImageNet labels) and
 * substring-matches against known glasses/sunglasses labels to flag photos
 * where the AI is unlikely to preserve identity well.
 *
 * Decision matrix:
 *   - hasSunglasses → block upload ("Remove sunglasses for better identity match")
 *   - hasClearGlasses → allow but warn ("Glasses detected — kiss may look slightly off")
 *   - neither → silent ok
 *
 * Singleton: pipeline is built ONCE per session via shared Promise. WebGPU is
 * preferred (faster) but Telegram WebView almost always falls back to WASM.
 *
 * Lazy import: @huggingface/transformers is a heavy dep (~3-5 MB minified +
 * ~5 MB ONNX runtime + model weights from HF CDN). Loaded only on first
 * detection call.
 *
 * Graceful failure: if the model fails to load (CDN down, browser too old,
 * etc.) the caller skips glasses validation entirely — do NOT block the
 * user's upload over our local ML problem.
 */

// Type-only import for the pipeline factory and the result shape.
import type { ImageClassificationPipeline } from '@huggingface/transformers';

let pipeline: ImageClassificationPipeline | null = null;
let loadingPromise: Promise<void> | null = null;
let backendLogged = false;

/**
 * HF model id. ONNX-converted, INT8-quantized, ~5 MB on first download then
 * cached by the browser. Trained on ImageNet-1k so its label space includes
 * 'sunglass'/'sunglasses' (n04356056) and 'spectacles' (n04272054).
 */
const MODEL_ID = 'onnx-community/mobilenetv4_conv_small.e2400_r224_in1k';

/**
 * Hard timeout — if HF CDN is dead we shouldn't block the photo upload
 * indefinitely. After this, caller treats it as "ML unavailable".
 */
const PIPELINE_LOAD_TIMEOUT_MS = 15_000;

/**
 * Substring-match keywords against ImageNet label strings. ImageNet labels
 * are comma-separated synonyms (e.g. "sunglass, dark glasses, shades") so
 * we lowercase + check for substring presence rather than exact match.
 *
 * Sunglasses keywords are stricter (lower priority for glasses detection)
 * — if a label matches BOTH a sunglass keyword and a glasses keyword,
 * sunglass wins.
 */
const SUNGLASS_KEYWORDS = ['sunglass', 'sunglasses', 'dark glasses', 'shades'] as const;
const GLASSES_KEYWORDS = ['spectacles', 'specs', 'eyeglasses', 'glasses'] as const;

/** Confidence threshold — must clear this for the keyword to count. */
const SUNGLASS_THRESHOLD = 0.10;
const GLASSES_THRESHOLD = 0.08;

async function ensurePipelineLoaded(): Promise<void> {
  if (pipeline) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const transformers = await import('@huggingface/transformers');

    // Prefer WebGPU when available (Chrome desktop on capable hardware).
    // Telegram WebView almost always lacks it → WASM fallback. Either path
    // works; WASM is slower (~1-2s per inference vs ~200ms WebGPU).
    let device: 'webgpu' | 'wasm' = 'wasm';
    if (
      typeof navigator !== 'undefined' &&
      (navigator as unknown as { gpu?: unknown }).gpu != null
    ) {
      device = 'webgpu';
    }
    if (!backendLogged) {
      // eslint-disable-next-line no-console
      console.log(`[glasses] using ${device} backend for HF transformers`);
      backendLogged = true;
    }

    // TS can't unify the pipeline() overload union (one signature per task);
    // cast through unknown to skip inference, then narrow at call site.
    const pipelineFactory = transformers.pipeline as unknown as (
      task: string,
      model: string,
      opts: { device: 'webgpu' | 'wasm' },
    ) => Promise<ImageClassificationPipeline>;
    const buildOp = pipelineFactory('image-classification', MODEL_ID, { device });

    const timeoutOp = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`HF pipeline load timed out after ${PIPELINE_LOAD_TIMEOUT_MS}ms`)),
        PIPELINE_LOAD_TIMEOUT_MS,
      ),
    );

    pipeline = await Promise.race([buildOp, timeoutOp]);
  })();

  try {
    await loadingPromise;
  } catch (e) {
    loadingPromise = null;
    throw e;
  }
}

export interface GlassesResult {
  hasSunglasses: boolean;
  hasClearGlasses: boolean;
}

/**
 * Run glasses detection on `file`. Returns flags for sunglasses + clear
 * glasses. Throws on model-load / inference failure — caller catches and
 * applies graceful degradation policy.
 */
export async function detectGlasses(file: File): Promise<GlassesResult> {
  await ensurePipelineLoaded();
  if (!pipeline) throw new Error('glasses pipeline not loaded');

  const url = URL.createObjectURL(file);
  try {
    const raw = (await pipeline(url, { topk: 20 } as never)) as Array<{
      label: string;
      score: number;
    }>;

    // eslint-disable-next-line no-console
    console.log('[glasses] top-20 ImageNet predictions', raw);

    let hasSunglasses = false;
    let hasClearGlasses = false;

    for (const p of raw) {
      const label = p.label.toLowerCase();
      const matchesSunglass =
        p.score >= SUNGLASS_THRESHOLD && SUNGLASS_KEYWORDS.some((k) => label.includes(k));
      if (matchesSunglass) {
        hasSunglasses = true;
        continue;
      }
      // Only count as "clear glasses" if it does NOT also match a sunglass
      // keyword — sunglass labels often contain "glasses" too ("dark glasses").
      const matchesGlasses =
        p.score >= GLASSES_THRESHOLD &&
        GLASSES_KEYWORDS.some((k) => label.includes(k)) &&
        !SUNGLASS_KEYWORDS.some((k) => label.includes(k));
      if (matchesGlasses) {
        hasClearGlasses = true;
      }
    }

    return { hasSunglasses, hasClearGlasses };
  } finally {
    URL.revokeObjectURL(url);
  }
}
